import { Modal, ModalBody, ModalFooter, ModalHeader, DropdownItem, DropdownMenu, ButtonDropdown, DropdownToggle, Button, Form } from 'reactstrap';
import { Row, Col, CardBody, Card, CardHeader, CardFooter, FormGroup, Label, Input, Popover, PopoverBody, PopoverHeader, Container } from 'reactstrap'
import React, { Fragment, useEffect, useState, useRef, cloneElement } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { Btn, H5, H4 } from '../../AbstractElements';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import buildRandomId, { lowercaseFirstLetter, handleTabOnKeyDown, findReactSelectValue } from '../Helpers';
import { FormCheck, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import FilterComponent from './FilterComponent';
import * as feather from 'react-feather';
import { ProgressLoader } from '../../Layout/Loader';
import Select from 'react-select';
import { useWatch } from 'react-hook-form';

const CardBuilder = ({ title, subTitle, body, footer }) => {

    footer ? footer : true

    return (
        <Card>
            <CardHeader>
                <H5>{title}</H5><span>{subTitle}</span>
            </CardHeader>
            <CardBody>
                {body}
            </CardBody>

            {footer ?
            <CardFooter>
                <Btn attrBtn={{ color: "primary", className: "m-r-15", type: "submit" }} >Submit</Btn>
                <Btn attrBtn={{ color: "light", type: "submit" }} >Cancel</Btn>
            </CardFooter> : null }
        </Card>
    )
}

const FormatError = (registratorErrors, name, label) => {

    if (!registratorErrors[name]){
        return null
    }

    let error = registratorErrors[name]

    let errorMessage = label + ' '

    switch (error.type)
    {
        case 'minLength':
        case 'maxLength':
            errorMessage += 'does not meet the length requirements'
        case 'validate':
            error.message
        case 'required':
            error.message += 'is required'
        default:
            errorMessage += 'is required'
    }

    console.error('Formatting Error:', errorMessage, error)

    // registratorErrors[name] = null

    return errorMessage

}

const RegisterInput = ({
    name, label, type, options, value, style, labelStyle, helpText, labelSize = 4, inputSize = 8, placeholder, required, popover_text,
    onChange, onKeyDown, registrator, registratorErrors, registrationSettings = {}, id = buildRandomId(7), visible = true, requiredInput = false,
    readOnly = false, addOn = null, settings={}, containerStyle={}, control = null, errorText = true
    }) => {

    if (!visible) return null

    const [showRedBorder, setShowRedBorder] = useState(false);

    let className = 'form-control'

    name = name ? name : label ? lowercaseFirstLetter(label).replaceAll(' ', '') : '';

    className = type === 'select' ? className + ' btn-square' : className

    // Handle unified options prop based on input type
    let selectOptions = null;
    if (options) {
        if (['reactselect', 'multiselect'].includes(type)) {
            selectOptions = options; // Use options directly for react-select
        } else if (type === 'select') {
            options = options; // Keep as options for regular select
        }
    }

    // Detect grouped react-select options ([{ label, options: [...] }]) so we only
    // indent/style group headings when groups actually exist (flat selects unaffected).
    const hasGroupedOptions = Array.isArray(selectOptions) && selectOptions.some(o => o && Array.isArray(o.options));

    required = required ? required : false

    // For select types, readOnly means prevent changes but allow viewing options
    const isSelectReadOnly = readOnly && ['select', 'reactselect', 'multiselect'].includes(type);

    if(!readOnly) readOnly = type === 'static' ? true : false

    if (type === 'textarea' && settings.handleTabs){
        onKeyDown = handleTabOnKeyDown
    }

    if (type === 'textarea' && settings.resize){
        let defaultStyle = { resize: 'both', height: 'fit-content', minHeight: 80, maxHeight: 450, minWidth: '100%', maxWidth: '110%' }
        style = style ? { ...defaultStyle, ...style } : defaultStyle
    }

    labelSize = labelSize ? labelSize : 4
    inputSize = inputSize ? inputSize : 8

    const customTextArea = settings?.customTextArea ? true : false
    const helpTextOffset = settings?.helpTextOffset ?? (customTextArea ? 0 : labelSize)

    /**
     * For some reason when unpacking the returned objects from the registrator
     * function, we have to use the variable name "ref". If not, the unpacking
     * doesn't work correctly. Thus, for each input we need to create the ref
     * dynamically as we can't define "ref" more than once.
     */
    const {ref,...registration} = registrator(name, {
        required: required,
        ...registrationSettings
    });

    // Combine all possible onChange handlers
    const combinedOnChange = (e) => {
        if (typeof onChange === 'function') {
            onChange(e);
        }
        if (typeof registration.onChange === 'function') {
            registration.onChange(e);
        }
    };

    // Use useWatch to get current form value if control is provided
    const watchedValue = control && ['reactselect', 'multiselect'].includes(type) ? useWatch({ control, name }) : registration.value;

    const customMultiSelect = settings?.customTextArea && type === 'multiselect' ? true : false;

    // Handle red border flash when validation fails
    useEffect(() => {
        if (!errorText && required && registratorErrors[name]) {
            setShowRedBorder(true);
            const timer = setTimeout(() => setShowRedBorder(false), 70000);
            return () => clearTimeout(timer);
        }
    }, [Object.keys(registratorErrors).length, errorText, required, name]);

    const inputClassName = showRedBorder ? `${className} input-error-border` : className;
    const selectStyles = showRedBorder ? {
        control: (base) => ({
            ...base,
            boxShadow: '0 0 0 1px rgba(255, 0, 0, 0.4) !important',
            transition: 'box-shadow 0.3s ease-in-out'
        }),
        ...(customMultiSelect ? { container: (base) => ({ ...base, width: style?.width || '140%', maxWidth: style?.maxWidth || '150%' }) } : {})
    } : {
        control: (base) => ({
            ...base,
            transition: 'border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
        }),
        // Make grouped options read clearly: prominent group header + indented options.
        groupHeading: (base) => ({
            ...base,
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#495057',
            background: '#f1f3f5',
            padding: '6px 10px',
            marginBottom: 2,
        }),
        option: (base) => ({
            ...base,
            ...(hasGroupedOptions ? { paddingLeft: 22 } : {}),
        }),
        ...(customMultiSelect ? { container: (base) => ({ ...base, width: style?.width || '140%', maxWidth: style?.maxWidth || '150%' }) } : {})
    };

    return (
        <Fragment>

            {settings?.topAligned ? (
                <Col sm={12} className='mb-0'>
                    <Label style={{ fontSize: 12, fontWeight: 600, marginBottom: 2, paddingLeft: 3 }}>{label}{ required || requiredInput ? <span style={{ color: "red" }}> *</span> : null }</Label>
                </Col>
            ) : (
                <Col sm={labelSize} className={settings?.compact ? '' : 'mb-2'} style={ labelStyle ? labelStyle : { textAlign: 'right'}}>
                    { required || requiredInput ? <span style={{ color: "red" }}> * </span> : null }
                    <Label className={settings?.compact ? '' : 'col-form-label'} style={settings?.compact ? { marginBottom: 0, fontSize: 13 } : {}}>{label}</Label>
                </Col>
            )}
            <Col sm={settings?.topAligned ? 12 : inputSize}>
                {type === 'multiselect' ? (
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        menuPortalTarget={document.body}
                        options={selectOptions}
                        placeholder={placeholder}
                        value={watchedValue ? watchedValue.map(val => {
                            const found = selectOptions.find(opt => opt.value === val);
                            return found || { value: val, label: `Unknown: ${val}` };
                        }) : []}
                        onChange={(selectedOptions) => {
                            if (isSelectReadOnly) return;
                            const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                            const syntheticEvent = { target: { name, value: values } };
                            registration.onChange(syntheticEvent);
                            combinedOnChange(syntheticEvent);
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                            ...selectStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            ...(settings?.compact ? {
                                option: (base) => ({ ...base, fontSize: 12, padding: '6px 10px' }),
                                menuList: (base) => ({ ...base, fontSize: 12 }),
                            } : {})
                        }}
                    />
                ) : type === 'reactselect' ? (
                    <Select
                        options={selectOptions}
                        placeholder={placeholder}
                        menuPortalTarget={document.body}
                        isClearable={settings?.isClearable || false}
                        formatOptionLabel={settings?.formatOptionLabel || undefined}
                        value={findReactSelectValue(
                            selectOptions,
                            watchedValue,
                            settings.treatZeroAsEmpty !== undefined ? settings.treatZeroAsEmpty : false,
                            settings.treatNullAsEmpty !== undefined ? settings.treatNullAsEmpty : true
                        )}
                        onChange={(selectedOption) => {
                            if (isSelectReadOnly) return;
                            const syntheticEvent = { target: { name, value: selectedOption ? selectedOption.value : '' } };
                            registration.onChange(syntheticEvent);
                            combinedOnChange(syntheticEvent);
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={settings?.compact ? {
                            control: (base) => ({ ...base, minHeight: 32, fontSize: 12 }),
                            option: (base) => ({ ...base, fontSize: 12, padding: '6px 10px', ...(hasGroupedOptions ? { paddingLeft: 20 } : {}) }),
                            groupHeading: (base) => ({ ...base, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#495057', background: '#f1f3f5', padding: '5px 10px', marginBottom: 2 }),
                            menuList: (base) => ({ ...base, fontSize: 12 }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            singleValue: (base) => ({ ...base, fontSize: 12 }),
                            placeholder: (base) => ({ ...base, fontSize: 12 }),
                            valueContainer: (base) => ({ ...base, padding: '0 6px' }),
                            dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                            clearIndicator: (base) => ({ ...base, padding: 4 }),
                        } : {
                            ...selectStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                    />
                ) : type !== 'separator' ?
                    (<><Input
                        key={name || id}
                        id={id}
                        className={inputClassName}
                        type={type}
                        style={style}
                        placeholder={placeholder}
                        {...registration}
                        innerRef={ref}
                        onChange={combinedOnChange}
                        onKeyDown={onKeyDown}
                        value={value}
                        readOnly={readOnly}
                    >
                        {options}
                    </Input>{addOn ? addOn : null}</>) : <hr/>}
            </Col>
            { helpText ? <span style={{ color: "gray", fontSize: 12, ...(settings?.topAligned ? { paddingLeft: 15, paddingTop: 5 } : { }) }} className={settings?.topAligned ? '' : 'offset-md-' + helpTextOffset}>{helpText} </span> : null }
            { !required || !errorText ? null : <span className='offset-md-1' style={{ color: "red", fontSize: 12 }}>{FormatError(registratorErrors, name, label)} </span> }

            {popover_text ?
                <PopoverBuilder item={{
                    id: id,
                    placement: 'right',
                    Popoverbody: popover_text,
                    btncolor: 'primary'
                }}/> : null}
        </Fragment>
    )
}

const RegisteredInputsBuilder = ({ inputs, registrator, registratorErrors={}, blockRequired = false, readOnly = false, control = null, errorText = false, form = null }) => {

    // If form object is passed, extract props from it (backwards compatible)
    if (form) {
        registrator = form.register;
        registratorErrors = form.formState?.errors || {};
        control = form.control;
    }

    return inputs.map(function(input){

        let { size = 12, visible = true, offset = 0 } = input

        let containerStyle = input.containerStyle ? input.containerStyle : {}

        if(!input?.readOnly && readOnly){
            input.readOnly = readOnly
        }

        if (input.settings && ['textarea', 'multiselect'].includes(input.type) && input.settings.customTextArea){
            if (input.offset === undefined) {
                offset = 1
                containerStyle = {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                }
                if (!input.labelStyle) input.labelStyle = { align:'left' }
            }
            input.customTextArea = true
        }

        if (input.type === 'custom' && input.render) {
            return (
                <Col md={{size, offset}} key={`custom-${index}`}>
                    {input.render}
                </Col>
            )
        }

        const inputIsCheckbox = input.type === 'checkbox' ? true : false

        if (!visible) return null

        return (
            <Col md={{size, offset}} key={input.name || `input-${index}`}>
                <FormGroup className="row" style={ inputIsCheckbox ? { display: 'flex', alignItems: 'center' } : containerStyle }>
                        <RegisterInput {...input} registrator={registrator} registratorErrors={registratorErrors} control={control} errorText={errorText}/>
                </FormGroup>
            </Col>
        )
    })
}

/**
 * Our API restricts endpoints to logged in Cognito users. In order to validate that the
 * requestor is a registered user, the apiToken is store in the localStorage. We have to
 * send that token in the header everytime we make a request.
 * 
 * This implementation bypasses the auto-generated SDK and makes direct fetch calls.
 * It parses the SDK method name to derive the HTTP method and URL path.
 */

const HTTP_VERBS = ['Get', 'Post', 'Patch', 'Delete', 'Put'];
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dev.portal.nexnow.io/v1';

/**
 * Detect direct path syntax and return { verb, path } or null if not a direct path.
 * Supported formats:
 *   'PATCH /dmt/jobs/{jobId}/keymappings/{resourceKeyId}'  → verb + path
 *   '/dmt/jobs/{jobId}/keymappings/{resourceKeyId}'        → path only (defaults to GET)
 *   'dmt/jobs/{jobId}/keymappings/{resourceKeyId}'         → path without leading slash (defaults to GET)
 */
function parseDirectPath(input) {
    if (!input || !input.includes('/')) return null;

    const VERBS = ['GET', 'POST', 'PATCH', 'PUT', 'DELETE'];
    const trimmed = input.trim();

    // Check for "VERB /path" format
    const spaceIdx = trimmed.indexOf(' ');
    if (spaceIdx > 0) {
        const maybeVerb = trimmed.slice(0, spaceIdx).toUpperCase();
        if (VERBS.includes(maybeVerb)) {
            let path = trimmed.slice(spaceIdx + 1).trim();
            if (!path.startsWith('/')) path = '/' + path;
            return { verb: maybeVerb, path };
        }
    }

    // Path only — starts with "/" or contains "/" (no verb prefix)
    let path = trimmed;
    if (!path.startsWith('/')) path = '/' + path;
    return { verb: 'GET', path };
}

/**
 * Parse an SDK method name into { verb, path }.
 * e.g. 'connectionsConnectionIdGet' → { verb: 'GET', path: '/connections/{connectionId}' }
 * e.g. 'oepOrdersOrderIdItemsItemIdPatch' → { verb: 'PATCH', path: '/oep/orders/{orderId}/items/{itemId}' }
 * e.g. 'dmtAssessmentsAssessmentIdRecordCountPost' → { verb: 'POST', path: '/dmt/assessments/{assessmentId}/recordCount' }
 * e.g. 'authEmulateStopPost' → { verb: 'POST', path: '/auth/emulate/stop' }
 */

// Known compound path segments (camelCase in the actual API path)
const COMPOUND_SEGMENTS = {
    'requestcode': 'requestCode',
    'verifycode': 'verifyCode',
    'filterablekeys': 'filterableKeys',
    'recordcount': 'recordCount',
    'setupvalues': 'setupValues',
    'globalsettings': 'globalSettings',
    'isolatedlogs': 'isolatedLogs',
    'keymappings': 'keymappings',
    'valuemappings': 'valuemappings',
    'compositemappings': 'compositeMappings',
    'supplementarydata': 'supplementaryData',
    'worksheetdefaults': 'worksheetDefaults',
    'mappingcodes': 'mappingCodes',
    'mappingcodesnew': 'mappingCodesNew',
    'scopetemplates': 'scopeTemplates',
    'bulkupdate': 'bulkUpdate',
    'chainoptions': 'chainOptions',
    'activitylog': 'activityLog',
    'deltastatus': 'deltaStatus',
};

// Known hyphenated path segments
const HYPHENATED_SEGMENTS = {
    'rateplans': 'rate-plans',
    'importorders': 'import-orders',
    'salesreps': 'sales-reps',
};

function parseMethodName(methodName) {
    // Extract HTTP verb from the end
    let verb = 'GET';
    for (const v of HTTP_VERBS) {
        if (methodName.endsWith(v)) {
            verb = v.toUpperCase();
            methodName = methodName.slice(0, -v.length);
            break;
        }
    }

    // Split on uppercase boundaries
    const parts = methodName.replace(/([A-Z])/g, ' $1').trim().split(' ');
    
    const pathSegments = [];
    let i = 0;
    
    while (i < parts.length) {
        // Check if this part + next part form a path param ({xxxId} or {xxxName})
        if (i + 1 < parts.length && parts[i + 1].toLowerCase() === 'id') {
            const paramName = parts[i].charAt(0).toLowerCase() + parts[i].slice(1) + 'Id';
            pathSegments.push(`{${paramName}}`);
            i += 2;
        } else if (i + 1 < parts.length && parts[i + 1].toLowerCase() === 'name') {
            const paramName = parts[i].charAt(0).toLowerCase() + parts[i].slice(1) + 'Name';
            pathSegments.push(`{${paramName}}`);
            i += 2;
        } else {
            // Try to match compound segments (2-3 words that form one path segment)
            let matched = false;
            for (let len = 3; len >= 2; len--) {
                if (i + len <= parts.length) {
                    const candidate = parts.slice(i, i + len).join('').toLowerCase();
                    if (COMPOUND_SEGMENTS[candidate]) {
                        pathSegments.push(COMPOUND_SEGMENTS[candidate]);
                        i += len;
                        matched = true;
                        break;
                    }
                    if (HYPHENATED_SEGMENTS[candidate]) {
                        pathSegments.push(HYPHENATED_SEGMENTS[candidate]);
                        i += len;
                        matched = true;
                        break;
                    }
                }
            }
            if (!matched) {
                pathSegments.push(parts[i].toLowerCase());
                i++;
            }
        }
    }
    
    return { verb, path: '/' + pathSegments.join('/') };
}

/**
 * Build the final URL by substituting path params.
 */
function buildUrl(pathTemplate, params, queryParams) {
    let url = pathTemplate;
    
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            url = url.replace(`{${key}}`, encodeURIComponent(value));
        }
    }
    
    const queryString = queryParams ? Object.entries(queryParams)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&') : '';
    
    if (queryString) {
        url += '?' + queryString;
    }
    
    return url;
}

const ApiRequestBuilder = (apiRequestMethod, requestParams = null, requestBody = null, requestOptions = null, queryParams = null) => {
    return new Promise((resolve, reject) => {
    
    if (!requestOptions){
        requestOptions = {};
    }

    try {
        const { verb, path } = parseDirectPath(apiRequestMethod) || parseMethodName(apiRequestMethod);
        const url = API_BASE_URL + buildUrl(path, requestParams, queryParams);
        
        const fetchOptions = {
            method: verb,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'authorization': localStorage.getItem('apiToken') || '',
            },
        };
        
        if (requestBody && verb !== 'GET') {
            fetchOptions.body = JSON.stringify(requestBody);
        }
        
        return fetch(url, fetchOptions)
            .then(res => {
                if (res.status === 401) {
                    if (!window.location.href.includes('login') && !window.location.href.includes('signup')) {
                        ToastBuilder('error', 'Please Login Again')
                        window.location.href = process.env.PUBLIC_URL + '/logout'
                    }
                    return resolve([]);
                }
                return res.json().then(data => {
                    const result = { data, status: res.status };
                    
                    requestOptions.callback ? requestOptions.callback() : null
                    requestOptions.toastOnSuccess ? ToastBuilder('success', requestOptions.toastOnSuccess) : null
                    requestOptions.setIsProcessing ? requestOptions.setIsProcessing(false) : null
                    
                    return resolve(result);
                }).catch(() => {
                    // Response wasn't JSON
                    const result = { data: null, status: res.status };
                    requestOptions.setIsProcessing ? requestOptions.setIsProcessing(false) : null
                    return resolve(result);
                });
            })
            .catch(err => {
                requestOptions.toastOnError ? ToastBuilder('error', requestOptions.toastOnError) : null
                requestOptions.setIsProcessing ? requestOptions.setIsProcessing(false) : null
                
                ToastBuilder('error', 'Unable to make request at this time')
                console.log('API Error:', err)
                
                return resolve([])
            })

    } catch (error) {
        console.error('API Error:', apiRequestMethod, error)
        ToastBuilder('error', 'Unable to make request at this time')
        return resolve([])
    }
    })
}

/**
 * ApiRequestBuilderV2 — Standardized API request wrapper.
 *
 * Always resolves with a consistent shape:
 *   { success: boolean, data: any, status: number, error: string|null, method: string }
 *
 * Never rejects. Check res.success to determine if the call worked.
 * Does NOT show toasts — caller decides how to handle errors.
 *
 * Supports two calling styles:
 *
 *   // Config object (recommended)
 *   ApiRequestBuilderV2('servicesGet')
 *   ApiRequestBuilderV2('dmtJobsJobIdGet', { pathParams: { jobId: '1' } })
 *   ApiRequestBuilderV2('dmtAssessmentsAssessmentIdRecordCountPost', {
 *       pathParams: { assessmentId: '1' },
 *       body: { connectionId: 202 },
 *       queryParams: { resourceId: '97' },
 *       setIsProcessing,
 *   })
 *
 *   // Positional (backwards compat)
 *   ApiRequestBuilderV2('servicesGet', pathParams, body, queryParams, options)
 */
const CONFIG_KEYS = new Set(['pathParams', 'body', 'queryParams', 'setIsProcessing', 'toastOnSuccess', 'toastOnError']);

const ApiRequestBuilderV2 = (apiRequestMethod, ...args) => {
    let pathParams = null, body = null, queryParams = null, setIsProcessing = null, toastOnSuccess = null, toastOnError = null;

    // Detect config object style: second arg is an object with known config keys
    if (args.length <= 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0]) && Object.keys(args[0]).some(k => CONFIG_KEYS.has(k))) {
        const config = args[0];
        pathParams = config.pathParams || null;
        body = config.body || null;
        queryParams = config.queryParams || null;
        setIsProcessing = config.setIsProcessing || null;
        toastOnSuccess = config.toastOnSuccess || null;
        toastOnError = config.toastOnError !== undefined ? config.toastOnError : null;
    } else {
        // Positional style
        pathParams = args[0] || null;
        body = args[1] || null;
        queryParams = args[2] || null;
        const options = args[3] || null;
        setIsProcessing = options?.setIsProcessing || null;
        toastOnSuccess = options?.toastOnSuccess || null;
        toastOnError = options?.toastOnError !== undefined ? options.toastOnError : null;
    }

    return new Promise((resolve) => {
        if (setIsProcessing) setIsProcessing(true);

        try {
            const { verb, path } = parseDirectPath(apiRequestMethod) || parseMethodName(apiRequestMethod);
            const url = API_BASE_URL + buildUrl(path, pathParams, queryParams);

            const fetchOptions = {
                method: verb,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'authorization': localStorage.getItem('apiToken') || '',
                },
            };

            if (body && verb !== 'GET') {
                fetchOptions.body = JSON.stringify(body);
            }

            fetch(url, fetchOptions)
                .then(res => {
                    return res.json().then(data => {
                        if (setIsProcessing) setIsProcessing(false);
                        const isEmpty = data === '' || data === null || data === undefined;
                        const backendSuccess = isEmpty ? false : (typeof data === 'object' && data !== null && 'success' in data ? data.success : true);
                        if (apiRequestMethod.toLowerCase().includes('patch')) {
                            console.log('[V2 PATCH DEBUG]', apiRequestMethod, { status: res.status, data, backendSuccess });
                        }
                        if (backendSuccess !== false && toastOnSuccess) ToastBuilder('success', toastOnSuccess);
                        if (backendSuccess === false && toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (data?.message || 'Request failed'));
                        resolve({
                            success: backendSuccess !== false,
                            data,
                            status: res.status,
                            error: backendSuccess === false ? (data?.message || 'Request returned success: false') : null,
                            method: apiRequestMethod,
                        });
                    }).catch(() => {
                        if (setIsProcessing) setIsProcessing(false);
                        resolve({ success: false, data: null, status: res.status, error: 'Failed to parse response', method: apiRequestMethod });
                    });
                })
                .catch(err => {
                    if (setIsProcessing) setIsProcessing(false);
                    if (apiRequestMethod.toLowerCase().includes('patch')) {
                        console.error('[V2 PATCH DEBUG CATCH]', apiRequestMethod, { message: err?.message, err });
                    }
                    if (toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (err.message || 'Request failed'));
                    if (err.message === 'Request failed with status code 401' || err.message === 'Unauthorized' || err.message === 'The incoming token has expired') {
                        if (!window.location.href.includes('login') && !window.location.href.includes('signup')) {
                            window.location.href = process.env.PUBLIC_URL + '/logout';
                        }
                    }
                    resolve({
                        success: false,
                        data: null,
                        status: 500,
                        error: err.message || 'Request failed',
                        method: apiRequestMethod,
                    });
                });
        } catch (error) {
            if (setIsProcessing) setIsProcessing(false);
            if (toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (error.message || 'Unknown error'));
            resolve({
                success: false,
                data: null,
                status: 0,
                error: error.message || 'Unknown error',
                method: apiRequestMethod,
            });
        }
    });
};

/**
 * ApiRequest — Clean API request function with explicit method and endpoint.
 *
 * @param {string} method - HTTP verb: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
 * @param {string} endpoint - API path from _config.json, e.g. '/dmt/jobs/{jobId}/keymappings/{resourceKeyId}'
 * @param {object} [options] - Optional configuration object:
 *   @param {object} [options.pathParams]      - Path parameter substitutions, e.g. { jobId: 11, resourceKeyId: 42 }
 *   @param {object} [options.body]            - Request body (ignored for GET)
 *   @param {object} [options.queryParams]     - Query string parameters
 *   @param {function} [options.setIsProcessing] - State setter — auto-called with true/false
 *   @param {string|false} [options.toastOnSuccess] - Toast message on success
 *   @param {string|boolean} [options.toastOnError] - Toast message on error (true uses backend message)
 *
 * @returns {Promise<{success: boolean, data: any, status: number, error: string|null}>}
 *
 * Never rejects. Always resolves with a consistent response shape.
 *
 * @example
 *   const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/keymappings/{resourceKeyId}', {
 *       pathParams: { jobId: 11, resourceKeyId: 205 },
 *       body: { code: 3, conditions: '{}' },
 *       toastOnSuccess: 'Key Mapping Updated',
 *   });
 *   if (res.success) { ... }
 */
const ApiRequest = (method, endpoint, options = {}) => {
    const { pathParams = null, body = null, queryParams = null, setIsProcessing = null, toastOnSuccess = null, toastOnError = null } = options;

    const verb = method.toUpperCase();
    let path = endpoint.trim();
    if (!path.startsWith('/')) path = '/' + path;

    return new Promise((resolve) => {
        if (setIsProcessing) setIsProcessing(true);

        try {
            const url = API_BASE_URL + buildUrl(path, pathParams, queryParams);

            const fetchOptions = {
                method: verb,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'authorization': localStorage.getItem('apiToken') || '',
                },
            };

            if (body && verb !== 'GET') {
                fetchOptions.body = JSON.stringify(body);
            }

            fetch(url, fetchOptions)
                .then(res => {
                    return res.json().then(data => {
                        if (setIsProcessing) setIsProcessing(false);
                        const isEmpty = data === '' || data === null || data === undefined;
                        const backendSuccess = isEmpty ? false : (typeof data === 'object' && data !== null && 'success' in data ? data.success : true);
                        if (backendSuccess !== false && toastOnSuccess) ToastBuilder('success', toastOnSuccess);
                        if (backendSuccess === false && toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (data?.message || 'Request failed'));
                        resolve({
                            success: backendSuccess !== false,
                            data,
                            status: res.status,
                            error: backendSuccess === false ? (data?.message || 'Request returned success: false') : null,
                        });
                    }).catch(() => {
                        if (setIsProcessing) setIsProcessing(false);
                        resolve({ success: false, data: null, status: res.status, error: 'Failed to parse response' });
                    });
                })
                .catch(err => {
                    if (setIsProcessing) setIsProcessing(false);
                    if (toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (err.message || 'Request failed'));
                    if (err.message === 'Request failed with status code 401' || err.message === 'Unauthorized' || err.message === 'The incoming token has expired') {
                        if (!window.location.href.includes('login') && !window.location.href.includes('signup')) {
                            window.location.href = process.env.PUBLIC_URL + '/logout';
                        }
                    }
                    resolve({
                        success: false,
                        data: null,
                        status: 0,
                        error: err.message || 'Request failed',
                    });
                });
        } catch (error) {
            if (setIsProcessing) setIsProcessing(false);
            if (toastOnError) ToastBuilder('error', typeof toastOnError === 'string' ? toastOnError : (error.message || 'Unknown error'));
            resolve({
                success: false,
                data: null,
                status: 0,
                error: error.message || 'Unknown error',
            });
        }
    });
};

toast.configure();

const ToastBuilder = (type, message = '', style = {}, autoclose = 1500) => {

    switch (type) {
        case 'success':
            toast.success(message, {
                style: style,
                position: toast.POSITION.TOP_CENTER,
                autoClose: autoclose,
                className: 'black-background success',
            });
            break;
        case 'info':
            toast.info(message, {
                style: style,
                position: toast.POSITION.TOP_CENTER,
                autoClose: autoclose,
                className: 'info',
            });
        break;
        case 'warn':
        case 'warning':
            toast.warn(message, {
                style: style,
                position: toast.POSITION.TOP_CENTER,
                autoClose: autoclose,
                className: 'warning',
            });
        break;
        case 'error':
            toast.error(message, {
                style: style,
                position: toast.POSITION.TOP_CENTER,
                autoClose: autoclose,
                className: 'black-background error',

            });
        break;
        default:
        break;
    }
}

const CommonModal = ({ isOpen, toggler, size, title, titleActions, bodyClass, style={}, children, formId = '', onSubmit = true, onSubmitLabel = 'Submit', customSubmit = null, formIsValid = true, formErrors = {}, validationToast = true, onCancelLabel = 'Cancel', onDelete = false, onDeleteLabel = 'Delete' }) => {

    const handleSubmitClick = () => {
        if (customSubmit) {
            customSubmit(toggler);
            return;
        }
        if (formIsValid) {
            toggler();
        } else {
            validationToast ? ToastBuilder('error', `Missing Required Fields`) : null;
        }
    };

    const modalForm = formId ? formId : title.replaceAll(' ', '-').toLowerCase() + '-form';

    return (
      <Modal isOpen={isOpen} toggle={toggler} size={size} centered style={style}>
        <ModalHeader toggle={toggler}>
          {titleActions ? (
            <div className='d-flex align-items-center justify-content-between w-100' style={{ paddingRight: 16 }}>
              <span>{title}</span>
              {titleActions}
            </div>
          ) : title}
        </ModalHeader>
        <ModalBody className={bodyClass}>
          {children}
        </ModalBody>
        <ModalFooter>
            {onDelete ? <ButtonBuilder label={onDeleteLabel} color='danger' className='me-auto' onClick={() => { onDelete(); toggler() }} useSpinner={false} /> : null}
            <ButtonBuilder label={onCancelLabel} color='secondary' onClick={toggler} useSpinner={false} />
            {onSubmit ? <ButtonBuilder label={typeof onSubmitLabel === 'function' ? onSubmitLabel() : (onSubmitLabel || 'Submit')} onClick={handleSubmitClick} useSpinner={false} form={modalForm} /> : null}
            {/* {onSubmit ? <ButtonBuilder label={onSubmitLabel} useSpinner={false} form={formId ? formId : title.replaceAll(' ', '-').toLowerCase() + '-form'} /> : null} */}

        </ModalFooter>
      </Modal>
    );
  };

const ModalBuilder = ({ modalTitle, menuItem }) => {
    const [modal, setModal] = useState(false);
    const toggle = (event) => {
      setModal(!modal);
    }
    return (
      <Fragment>
        <a href="javascript"
            id="nav-link"
            className={`nav-submenu menu-content`}
            onClick={(event) => {
              event.preventDefault();
              toggle();
            }} >
            {menuItem.icon !== undefined && <menuItem.icon />}
            <span>{menuItem.title}</span>
        </a>
        <CommonModal isOpen={modal} title={modalTitle} toggler={toggle} >
            {menuItem.modal}
        </CommonModal>
      </Fragment>
    );
  };

const FormOptionsBuilder = ({options, isGroup = false}) => {

    if (isGroup)
    {
        return Object.keys(options).map(group => {
            return (
                <optgroup key={group} label={group}>
                    {options[group].map(items => (
                        <option value={items.value} key={buildRandomId(7)}>{items.label}</option>
                    ))}
                </optgroup>
            )
        })
    }

    return options.map((items, i) =>
        <option value={items.value} key={buildRandomId(7)}>{items.label}</option>
    )
  }

  const ModalLinkBuilder = ({ label = '', customLabel = null, title = '', titleActions = null, body = null, className = null, formId='', onClick = null, size = 'sm', style = {}, onSubmit = true, onSubmitLabel, onCancelLabel, onDelete = false, onDeleteLabel }) => {
    const [modal, setModal] = useState(false);
    const toggle = (event) => {
        setModal(!modal);
    }
    return (
        <Fragment>
            <a href="javascript"
                className={className}
                onClick={(event) => {
                    event.preventDefault();
                    onClick ? onClick() : null;
                    toggle();
                }} >
                {customLabel ? customLabel : <span>{label}</span>}
            </a>
            <CommonModal isOpen={modal} title={title} titleActions={titleActions} toggler={toggle} size={size} formId={formId} style={style} onSubmit={onSubmit} onSubmitLabel={onSubmitLabel} onCancelLabel={onCancelLabel} onDelete={onDelete} onDeleteLabel={onDeleteLabel} >
                {body}
            </CommonModal>
        </Fragment>
    );
};

  const ModalButtonBuilder = ({ title = '', modalTitle = null, body = null, className = null, color = 'primary', formId = '', formIsValid = true, formErrors = {}, type = 'submit', onClick = null, size = 'large', style = {}, buttonStyle = {},
    onSubmit = true, onSubmitLabel, customSubmit = null, onCancelLabel, onDelete = false, onDeleteLabel, disabled=false }) => {
    const [modal, setModal] = useState(false);
    const toggle = () => {
        if (disabled) return;
        setModal(prev => !prev);
    }

    return (
      <Fragment>
        <ButtonBuilder
            label={title}
            useSpinner={false}
            formId={formId}
            type={type}
            style={{ position: 'relative', zIndex: 3, ...buttonStyle }}
            color={color}
            className={className ? className : 'col-md-2'}
            onClick={() => {
                toggle();
                onClick ? onClick() : null;
            }}
        />
        <CommonModal isOpen={modal} title={modalTitle ? modalTitle : title} toggler={toggle} formId={formId} size={size} style={style} onSubmit={onSubmit} formIsValid={formIsValid} formErrors={formErrors} onSubmitLabel={onSubmitLabel} customSubmit={customSubmit} onCancelLabel={onCancelLabel} onDelete={onDelete} onDeleteLabel={onDeleteLabel}>
            {body}
        </CommonModal>
      </Fragment>
    );
  };

const ButtonBuilder = ({label = 'Submit', type = 'submit', color = 'primary', size = 'sm', style = {}, className = '', form = null, useSpinner = true, onClick = null, onClickPromise = null, disabled = false}) => {

    const [spinner, setSpinner] = useState(false);

    if (useSpinner){
        style={'minWidth': label.length * 8, ...style }
    }

    const onButtonClick = () => {
        /**
         * Some places where a button is used may not
         * need a spinner, like launching a modal
         */
        if (useSpinner)
        {
            setSpinner(!spinner);
        }

        /**
         * If we were given a function to call, call it here
         * and then shut off the spinner. In order for the spinner
         * to turn off at the right time, the onClickPromise function must
         * return a promise.
         */
        if (onClickPromise)
        {
            onClickPromise().then( () => setSpinner(false) )
        }
        else
        {
            if (onClick) onClick()
            setTimeout( () => { try{ setSpinner(false) }catch{ } }, 2000 )
        }
    }

    return (
        <Button className={`btn ${className}`} color={color} size={size} type={type} onClick={onButtonClick} form={form} style={style} disabled={disabled}>
            {spinner ? <Spinner animation="border" size="sm" role="status"></Spinner> : label}
        </Button>
    )
}

const VerticalButtonGroup = ({ label = 'Show Options', className = 'btn-primary', color = null, style={}, buttons, settings = {}, dropdownStyle = {} }) => {
    const [showExtra, setShowExtra] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const displaySpeedFactor = settings?.speedFactor ? settings.speedFactor : 0.25

    const defaultButtons = [
        <Button color={null} size={'sm'} className="btn-outline-primary">Option A</Button>,
        <Button color={null} size={'sm'} className="btn-outline-secondary">Option B</Button>,
        <Button color={null} size={'sm'} className="btn-outline-dark">Option C</Button>,
        <Button color={null} size={'sm'} className="btn-outline-danger">Option D</Button>,
    ];

    const closeDropdown = () => setShowExtra(false);

    const buttonList = buttons && buttons.length > 0 ? buttons : defaultButtons;

    const renderedButtons = buttonList.map((btn, index) => {

        if (!btn) return
        const key = btn.key || `dropdown-btn-${index}`;
        const originalOnClick = btn.props?.onClick;
        const enhancedOnClick = (event) => {
            closeDropdown();
            setTimeout(() => {
                originalOnClick ? originalOnClick(event) : null;
            }, 500);
        };

        return cloneElement(btn, {
            ...btn.props,
            color:null,
            key,
            onClick: enhancedOnClick,
        });
    });

    const toggleExtra = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const pos = {
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            };
            if (dropdownStyle.position === 'left') {
                pos.left = rect.right + window.scrollX;
            }
            setPosition(pos);
        }
        setShowExtra(prev => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedInsideButton = buttonRef.current?.contains(event.target);
            const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

            if (!clickedInsideButton && !clickedInsideDropdown) {
                setShowExtra(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <>
            <Button
                innerRef={buttonRef}
                className={`${className}`}
                onClick={toggleExtra}
                size='sm'
                style={{minWidth: 150, ...style}}
                color={color}
            >
                {`${label}${settings?.hideArrow ? '' : ' ⯆'}`}
            </Button>

            {
                ReactDOM.createPortal(
                    <div
                        ref={dropdownRef}
                        className={`position-absolute vertical-button-group rounded shadow p-2 d-flex flex-column gap-1 ${showExtra ? 'opacity-100' : 'opacity-0'}`}
                        style={{
                            top: position.top,
                            left: position.left,
                            width: dropdownStyle.width !== undefined ? dropdownStyle.width : position.width,
                        minWidth: dropdownStyle.minWidth || undefined,
                            visibility: showExtra ? 'visible' : 'hidden',
                            transition: showExtra ? `opacity ${displaySpeedFactor * 3}s ease-in, transform ${displaySpeedFactor * 2}s ease-in` : `opacity ${displaySpeedFactor}s ease-out, transform ${displaySpeedFactor * 2}s ease-out`,
                            transform: dropdownStyle.position === 'left'
                                ? (showExtra ? 'translateX(-100%)' : `translateX(-100%) translateY(-${position.height}px)`)
                                : (showExtra ? 'translateY(0)' : `translateY(-${position.height}px)`),
                            pointerEvents: showExtra ? 'auto' : 'none',
                        }}
                    >
                        {renderedButtons}
                    </div>,
                    document.body
                )}
        </>
    );
};

const asyncPipeline = (...fns) => input =>
    fns.reduce((chain, fn) => chain.then(fn), Promise.resolve(input))


const DropdownBuilder = ({items, label}) => {

    // ButtonDropdown open state
    const [dropdownOpen, setOpen] = useState(false);

    const toggleDropdown = () => {
        setOpen(prev => !prev);
    };

    return (
        <div className='dropdown'>
            <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle color='black' caret>
                    {label}
                </DropdownToggle>
                <DropdownMenu style={{minWidth: '100%'}}>
                    {items.map((item, i) => <DropdownItem key={i} onClick={item.onClick}>{item.label}</DropdownItem>)}
                </DropdownMenu>
            </ButtonDropdown>
        </div>
    );
};

const PopoverBuilder = ({ item }) => {
    const [popover, setPopover] = useState(false);
    const Toggle = () => setPopover(!popover);
    return (
        <>
        <Popover
            placement={item.placement}
            isOpen={popover}
            target={item.id}
            toggle={Toggle}
            offset={item.offset}
            trigger={item.trigger}

        >
            <PopoverHeader>{item.Popoverheader}</PopoverHeader>
            <PopoverBody>
            {item.Popoverbody}
            </PopoverBody>
        </Popover>
        </>
    );
}

const customStyles = {
    headCells: {
        style: {
            '&:not(:last-of-type)': {
                borderRightStyle: 'solid',
                borderRightWidth: '2px',
                borderRightColor: 'rgba(51,51,51,.2)',
            },
        },
    },
};

/**
 * filterTableData — Search/filter logic for TableBuilder.
 *
 * Supports:
 *   word          — partial match (any column contains "word")
 *   "exact"       — exact match (a column value equals "exact" exactly)
 *   !word         — negation (exclude rows where any column contains "word")
 *   !"phrase"     — negated phrase (exclude rows where any column contains "phrase")
 *
 * Multiple terms are AND'd together. Each term must satisfy at least one column.
 *
 * Examples:
 *   network "Approved"       → contains "network" AND has a column exactly "Approved"
 *   network !deleted         → contains "network" AND no column contains "deleted"
 *   !"Help Desk" Incident   → no column contains "Help Desk" AND a column contains "Incident"
 *
 * Notes:
 *   - Quotes on positive terms → exact match (===)
 *   - Quotes on negated terms → just groups multi-word phrases (still partial .includes())
 *   - ! only triggers negation at the START of a token (mid/end ! is literal)
 *   - Column values are .toString().toLowerCase() before comparison
 */
const filterTableData = (data, filterText, columnsToSearch) => {
    if (!filterText) return data;

    return data.filter(item => {
        // Parse search terms: quoted phrases stay together, unquoted words split by space
        // Prefix with ! to negate: !word or !"exact phrase"
        const terms = [];
        const regex = /!?"([^"]*)"|!?([^\s]+)/g;
        let match;
        while ((match = regex.exec(filterText)) !== null) {
            const raw = match[0];
            const negate = raw.startsWith('!');
            const text = (match[1] !== undefined ? match[1] : match[2]).replace(/^!/, '').toLowerCase();
            if (text) terms.push({ text, exact: match[1] !== undefined, negate });
        }
        if (terms.length === 0) return true;

        // Every term must match at least one column (AND logic)
        return terms.every(term => {
            const matches = columnsToSearch.some(column => {
                let value;
                if (column.includes('/')) {
                    let key = column.split('/')[0];
                    let subKey = column.split('/')[1];
                    value = item[key]?.[subKey];
                } else {
                    value = item[column];
                }
                if (!value) return false;
                const strValue = value.toString().toLowerCase();
                // Negated terms always use partial match (quotes just group multi-word phrases)
                if (term.negate) return strValue.includes(term.text);
                return term.exact ? strValue === term.text : strValue.includes(term.text);
            });
            return term.negate ? !matches : matches;
        });
    });
};

// https://react-data-table-component.netlify.app/?path=/docs/api-props--docs
const TableBuilder = ({
    data, title, columns, actions,
    columnsToSearch = [], subHeader = false,
    processing = true, progressComponent = null,
    pagination = true, pageSize, paginationPerPage = 10, paginationRowsPerPageOptions,
    sortField = 1, sortOrderAsc = true, onSortChange = null,
    styles = {}, conditionalRowStyles,
}) => {


    paginationPerPage = pageSize || paginationPerPage;

    const [filterText, setFilterText] = React.useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);

    const filteredItems = filterTableData(data, filterText, columnsToSearch);

    const subHeaderComponentMemo = React.useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };

        return (
            <FilterComponent onFilter={e => setFilterText(e.target.value)} onClear={handleClear} filterText={filterText} />
        );
    }, [filterText, resetPaginationToggle]);

    // Notify parent when filtered data changes (for bulk select sync)
    React.useEffect(() => {
        if (onSortChange) onSortChange(filteredItems)
    }, [filteredItems])

    const handleSort = onSortChange ? (column, direction) => {
        const sorted = [...filteredItems].sort((a, b) => {
            const result = column.sortFunction
                ? column.sortFunction(a, b)
                : String(column.selector(a)).localeCompare(String(column.selector(b)))
            return direction === 'asc' ? result : -result
        })
        onSortChange(sorted)
    } : undefined

    return (
        <DataTable
            dense
            title={title}
            progressPending={processing}
            progressComponent={progressComponent ? progressComponent : <ProgressLoader/>}
            pagination={pagination}
            paginationPerPage={paginationPerPage}
            paginationRowsPerPageOptions={paginationRowsPerPageOptions}
            paginationComponentOptions={{
                selectAllRowsItem: true
            }}
            defaultSortFieldId={sortField}
            defaultSortAsc={sortOrderAsc}
            customStyles={{...customStyles, ...styles}}
            subHeader={subHeader}
            subHeaderComponent={subHeaderComponentMemo}
            data={filteredItems}
            center={true}
            striped={true}
            actions={actions}
            columns={columns}
            persistTableHead
            highlightOnHover={true}
            onSort={handleSort}
            conditionalRowStyles={conditionalRowStyles}
        />
    );
}

const TextTitleBuilder = (text, style={fontWeight:'normal'}) => {

    return <span title={text} style={style}>{text}</span>
}

const Tile = ({ tile }) => {

    const Icon = feather[tile.icon]
    const BackgroundIcon = tile.backgroundIcon ? feather[tile.backgroundIcon] : Icon
    const title = tile.link ? <Link className='tile-title-link' to={`${tile.link}`}>{tile.title}</Link> : tile.title

    var content = null

    if (tile.count){
        content = <H4 attrH4={{ className: 'mb-0 tile-counter' }} >{tile.count}</H4>
    } else if(tile.content){
        content = <H4 attrH4={{ className: 'mb-0 tile-content' }} >{tile.content}</H4>
    }

    const footerStyle = { border: '0px solid blue', position: 'absolute', left: '10px', bottom: '5px', fontSize: '10px', color: 'gray' }

    return (
        <div className="media static-top-widget">
            <div className="align-self-center text-center">
                <Icon />
            </div>
            <div className="media-body">
                <span className="m-0 tile-title">{title}</span>
                {content}
                <BackgroundIcon className="icon-bg" />
            </div>
            <div className="media-footer" style={footerStyle}>
                {tile.footNote}
            </div>
        </div>
    )
}

const TilesBuilder = ({ data, container = true, fluid = false }) => {
    if (!data) {
        return null;
    }
    const visibleTiles = data.filter(element => !element.omit)
    const colSize = visibleTiles.length >= 5 ? true : 3 // auto-size for 5+, otherwise 3 (4 per row)
    const content = (
        <Row>
            {
                visibleTiles.map((tile) => {
                    return (
                        // Responsive column sizing: determines how many grid columns (out of 12) each tile occupies at different viewport sizes
                        <Col xl={colSize} lg={4} md={6} sm={10} className={`${container ? 'px-1' : ''}`} key={tile.id ? tile.id : buildRandomId(7)} >
                            <Card className={`o-hidden border-0 ${container ? 'px-1' : ''}`}>
                                <CardBody className="px-1">
                                    <Tile tile={tile} />
                                </CardBody>
                            </Card>
                        </Col>
                    )
                })
            }
        </Row>
    )
    return container ? <Container fluid={fluid} className="m-l-0">{content}</Container> : content;
}

const HoverOver = ({ content, text, textStyle = {}, contentPrefix = null, callback = null, callbackContent = null, keepHovered = true, contentStyle = {}, copyable = false }) => {

    const [isHovered, setIsHovered] = useState(false);
    const [componentPosition, setComponentPosition] = useState({ top: 0, left: 0, height: 100 });
    const containerRef = useRef(null);
    const bubbleRef = useRef(null);
    const bubbleContainer = useRef(document.createElement('div'));
    const [minWidth, setMinWidth] = useState(100);
    const maxHeight = 350;
    // let isJSON = false;
    const isJSON = content !== null && typeof content === 'object' ? true : false;

    const containerStyle = {
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 80,
        minHeight: '20px',
        wordWrap: 'break-word',
        textAlign: 'center',
        position: 'relative',
    };

    const bubbleStyle = {
        position: 'absolute',
        top: componentPosition.top,
        left: componentPosition.left,
        transform: 'translateX(-50%)',
        padding: '15px 30px',
        minWidth: minWidth,
        maxWidth: 750,
        maxHeight: maxHeight,
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        color: '#fff',
        borderRadius: 5,
        whiteSpace: 'normal',
        zIndex: 9,
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
        // opacity: isHovered ? 0.95 : 0,
        opacity: isHovered ? componentPosition.opacity : 0,
        pointerEvents: keepHovered ? 'auto' : 'none',
        textAlign: 'left',
        overflow: 'auto',
        ...contentStyle
    };


    // The content is text, there may be new lines, tabs, or a dict. Replace with appropriate HTML
    const formatContent = (content) => {

        let contentPrefixText = '';

        if (contentPrefix){
            contentPrefixText = contentPrefix + '\n\n';
        }

        if (content !== null && typeof content === 'object' ) {
            const jsonStr = JSON.stringify(content, null, 4);
            if (copyable) {
                return <div style={{ whiteSpace: 'pre-wrap' }}>{contentPrefixText}{jsonStr}</div>
            }
            return <div style={{ whiteSpace: 'pre-wrap' }}>{contentPrefixText}{jsonStr}</div>
        }

        content = contentPrefixText + content;

        // Split the text by newlines
        return content.split('\n').map((line, index) => (
            // Replace occurrences of 4 spaces or tabs with a tab equivalent (e.g., &emsp; or &nbsp;)
            <React.Fragment key={index}>
                {line.split(/( {4}|\t)/).map((part, idx) =>
                    part === '    ' || part === '\t' ? <span key={idx}>&emsp;</span> : part
                )}
                <br />
            </React.Fragment>
        ));
    };

    const handleMouseEnter = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const scrollTop = window.scrollY;
            const scrollLeft = window.scrollX;

            setIsHovered(true);

            setTimeout(() => {
                if (bubbleRef.current) {
                    const bubbleRect = bubbleRef.current.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;

                    let top = rect.bottom + scrollTop - 5; // default: below
                    if (rect.bottom + bubbleRect.height > viewportHeight) {
                        top = rect.top + scrollTop - bubbleRect.height + 5; // move above
                    }
                    if (parseInt(bubbleRect.height) > (maxHeight / 2) ) {
                        setMinWidth(250)
                    } else if (parseInt(bubbleRect.height) > (maxHeight / 3) ) {
                        setMinWidth(175)
                    }

                    setComponentPosition({
                        top,
                        left: rect.left + scrollLeft + rect.width / 2,
                        height: rect.height,
                        opacity: .95
                    });
                }
            }, 0);
        }
    };


    const handleMouseLeave = () => {
        setIsHovered(false);
        setComponentPosition({opacity:0})
    };


    useEffect(() => {
        document.body.appendChild(bubbleContainer.current);
        return () => {
            document.body.removeChild(bubbleContainer.current);
        };
    }, []);


    return (
        <div className='d-flex flex-column' style={{ ...containerStyle, ...textStyle }}
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={keepHovered ? () => setIsHovered(false) : null}
        // onClick= {onClick ? onClick(content) : null}
        >
            {text}
            {isHovered && content &&
                createPortal(
                    <div ref={bubbleRef} style={bubbleStyle}>
                        { keepHovered ?
                            <span style={{ fontSize: 10, color: 'gray', float:'right' }}>(Double click to close{isJSON ? (copyable ?
                                <span style={{ color: 'green', fontWeight: 700, cursor: 'pointer' }} title='Click to copy JSON' onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(JSON.stringify(content, null, 4));
                                    const el = e.currentTarget;
                                    const orig = el.textContent;
                                    el.textContent = ' Copied!';
                                    setTimeout(() => { el.textContent = orig; }, 1000);
                                }}> JSON ⧉</span>
                                : <span style={{ color: 'green', fontWeight: 700 }}> JSON</span>
                            ) : ''})</span>
                            :
                            isJSON ?
                            <span style={{ fontSize: 10, color: 'green', float:'right', fontWeight: 700 }}>JSON</span>
                            : ''
                        }
                        <br/>
                        {formatContent(content)}
                        {/* <br/> */}
                    </div>,
                    bubbleContainer.current
                )
            }

            {/* {callback ? callback(callbackContent) : null} */}
        </div>
    );
};

const CheckBox = ({ reference=[], id, checked, onChange, style = {}, className = '' }) => {

    const [isChecked, setIsChecked] = useState(false)

    useEffect(() => {
        if (Array.isArray(reference)) {
            let idd = parseInt(id.replaceAll('checkbox-', ''))
            setIsChecked(reference.includes(idd))
        } else {
            setIsChecked(!!checked)
        }
    }, [checked])

    return (
        <FormCheck type={'checkbox'} id={id} checked={isChecked} onChange={onChange} style={style} className={className} />
    );
};

/**
 * PairedSelectGroup - Renders side-by-side select rows with a shared header.
 * 
 * @param {string} leftHeader - Left column header (e.g. 'Source')
 * @param {string} rightHeader - Right column header (e.g. 'Destination')
 * @param {Array} rows - Array of row configs: { label, leftOptions, rightOptions, leftValue, rightValue, onLeftChange, onRightChange, isDisabled }
 * @param {string} message - Optional message to display (e.g. 'Loading...')
 * @param {object} style - Optional container style overrides
 */
const SectionGroup = ({ title = null, children, style = {}, className = '' }) => {
    return (
        <div className={className} style={{ borderRadius: 6, padding: '4px 0', marginBottom: 6, ...style }}>
            {title && <Label className='section-group-title' style={{ fontWeight: 600, fontSize: 13, margin: '0 0 6px 2px' }}>{title}</Label>}
            {children}
        </div>
    );
};

const PairedSelectGroup = ({ leftHeader = 'Source', rightHeader = 'Destination', rows = [], message = null, style = {} }) => {
    return (
        <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '12px 14px', marginBottom: 8, ...style }}>
            <Row className='mb-2'>
                <Col md={5} className='text-center'>
                    <Label style={{ fontWeight: 600, fontSize: 13, color: '#555', margin: 0 }}>{leftHeader}</Label>
                </Col>
                <Col md={2} className='text-center'>
                    <span style={{ color: '#aaa', fontSize: 14 }}>→</span>
                </Col>
                <Col md={5} className='text-center'>
                    <Label style={{ fontWeight: 600, fontSize: 13, color: '#555', margin: 0 }}>{rightHeader}</Label>
                </Col>
            </Row>
            {rows.map((row, i) => (
                <Row className={i < rows.length - 1 ? 'mb-2' : ''} key={row.label || i}>
                    <Col md={6}>
                        <Label style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{row.label}</Label>
                        <Select
                            options={row.leftOptions || []}
                            value={row.leftValue || null}
                            onChange={row.onLeftChange}
                            placeholder={row.placeholder || 'Select...'}
                            isDisabled={row.isDisabled || false}
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                    </Col>
                    <Col md={6}>
                        <Label style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{row.label}</Label>
                        <Select
                            options={row.rightOptions || []}
                            value={row.rightValue || null}
                            onChange={row.onRightChange}
                            placeholder={row.placeholder || 'Select...'}
                            isDisabled={row.isDisabled || false}
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                    </Col>
                </Row>
            ))}
            {message && <p style={{ textAlign: 'center', color: '#888', fontSize: 12, margin: '8px 0 0' }}>{message}</p>}
        </div>
    );
};

const LabelWithSubtext = ({ text, subtext, align = 'left', onClick = null }) => (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'inherit', textAlign: align }}>
        <div>{text || ''}</div>
        {subtext ? <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{subtext}</div> : null}
    </div>
);

export default TableBuilder;

const ActionGroupBuilder = ({
    isApproved,
    disabled,
    onApprove,
    onDelete,
    modalTitle,
    modalBody,
    showBurger = true,
    approveWidth = 110,
    approveLabel,
    unapproveLabel,
    burgerLabel = '☰',
    className,
}) => {
    const baseColor = isApproved ? 'danger' : 'primary';
    const outlineClass = `btn-outline-${baseColor}`;
    const solidClass = `btn-${baseColor}`;
    const burgerWidth = 35;

    return (
        <div className={`btn-group my-1 ${className || ''}`} style={{ gap: 0 }}>
            <ButtonBuilder
                label={approveLabel || unapproveLabel ? (isApproved ? (unapproveLabel || 'Unapprove') : (approveLabel || 'Approve')) : (isApproved ? 'Unapprove' : 'Approve')}
                className={outlineClass}
                style={{ width: approveWidth }}
                useSpinner={false}
                disabled={disabled}
                onClick={onApprove}
            />
            {showBurger ? (
                <ModalButtonBuilder className={`${solidClass} btn-sm m-0`}
                    title={burgerLabel}
                    modalTitle={modalTitle}
                    onDelete={onDelete}
                    buttonStyle={{ minWidth: 0, width: burgerWidth, padding: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    body={modalBody}
                />
            ) : (
                <ButtonBuilder
                    label=''
                    className={`${solidClass} btn-sm`}
                    style={{ minWidth: 0, width: burgerWidth, padding: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    disabled={true}
                    useSpinner={false}
                />
            )}
        </div>
    );
};

import FlexInputBuilder from './FlexInputBuilder';

export {
    CardBuilder,
    RegisteredInputsBuilder,
    FlexInputBuilder,
    ApiRequestBuilder,
    ApiRequestBuilderV2,
    ApiRequest,
    ToastBuilder,
    ButtonBuilder,
    VerticalButtonGroup,
    ModalBuilder,
    PopoverBuilder,
    DropdownBuilder,
    ModalButtonBuilder,
    FormOptionsBuilder,
    ModalLinkBuilder,
    TextTitleBuilder,
    TilesBuilder,
    HoverOver,
    CheckBox,
    PairedSelectGroup,
    SectionGroup,
    asyncPipeline,
    CommonModal,
    LabelWithSubtext,
    ActionGroupBuilder
}